import * as React from 'react';
import { AlertTriangle, FileX } from 'lucide-react';

import { Aide } from '../ui/aide';
import { ChiffresCles, Question } from '../ui/chiffres-cles';
import { EmptyState } from '../ui/empty-state';
import { BarrePagination } from '../ui/pagination-bar';
import { formatDate } from '../../lib/formats';
import { usePagination } from '../../hooks/usePagination';
import { LEXIQUE } from '../../lib/lexique';
import { cn } from '../../lib/utils';
import { PastillePrevu, formaterResultat } from './TableauRapport';
import { EncartControleRecettes, Montant } from './VisuelsRapport';
import { libelleStock, montant, nombre, pluriel } from './reportHelpers';

/**
 * Bilan du mois, lu d'un coup d'œil puis par questions:
 *
 *  1. l'essentiel en quatre chiffres (résultat du mois, argent disponible, ce
 *     que les clients vous doivent, ce que vous devez), chacun avec un mot
 *     d'état;
 *  2. « Qu'est-ce que j'ai gagné ? »: le relevé qui descend (ventes de
 *     carburant HT moins coût du carburant vendu, marge carburant, plus le
 *     fret et la location, moins chaque charge, égale le résultat), le mois
 *     et le cumul de l'exercice côte à côte, lignes à zéro repliées. Les
 *     chiffres « pour information » (fret interne, achats de carburant,
 *     litres vendus, taxes) sont repliés sous le relevé, dans les mêmes
 *     colonnes; l'écart de contrôle ne se montre que s'il n'est pas nul;
 *  3. « Où est mon argent ? »: la position en tuiles, sans tuile orpheline;
 *  4. « Qu'est-ce qui arrive bientôt ? »: les papiers à échéance.
 *
 * Aucune note d'explication sur la page: les notes du serveur et le mode de
 * calcul s'ouvrent depuis l'icône d'aide de chaque question.
 *
 * Un bilan figé avant ce changement (ligne « Achats de carburant » dans le
 * relevé, pas de stock dans la position) s'affiche tel qu'il a été clôturé.
 *
 * Chaque libellé arrive déjà en français. `nature` ne sert qu'à la mise en
 * forme: produit (+), charge (−), sous-total, résultat.
 */

const TITRE_PREVU = 'Montant prévu, pas encore facturé';

const SIGNE = { produit: '+', charge: '−' };

const estRepliable = (ligne) =>
  (ligne.nature === 'produit' || ligne.nature === 'charge') &&
  montant(ligne.month) === 0 &&
  montant(ligne.cumulative) === 0;

const LigneReleve = ({ ligne, max }) => {
  const { nature } = ligne;
  const sousTotal = nature === 'sous_total';
  const total = nature === 'total';
  const prevu = Boolean(ligne.estimated) && (montant(ligne.month) !== 0 || montant(ligne.cumulative) !== 0);
  const part = max > 0 && !sousTotal && !total ? (Math.abs(montant(ligne.month)) / max) * 100 : 0;
  return (
    <li
      className={cn(
        'grid grid-cols-[1fr_auto_auto] items-center gap-x-4 px-4 py-2 text-sm sm:grid-cols-[1fr_9rem_9rem]',
        sousTotal && 'border-t border-border bg-muted/40 font-semibold',
        total && 'border-t-2 border-foreground/20 py-3 text-base font-bold',
      )}
    >
      <span className="min-w-0">
        <span className="flex items-center gap-2">
          {SIGNE[nature] && (
            <span
              className={cn(
                'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-[11px] font-semibold',
                nature === 'produit' ? 'bg-success/15 text-success' : 'bg-destructive/10 text-destructive',
              )}
              aria-hidden="true"
            >
              {SIGNE[nature]}
            </span>
          )}
          <span className={cn(sousTotal || total ? '' : 'text-foreground')}>{ligne.label}</span>
          {prevu && <PastillePrevu title={TITRE_PREVU} />}
        </span>
        {part > 0 && (
          <span className="ml-6 mt-1 block h-1 max-w-[16rem] rounded-full bg-muted" aria-hidden="true">
            <span
              className={cn('block h-full rounded-full', nature === 'produit' ? 'bg-success/70' : 'bg-destructive/50')}
              style={{ width: `${part}%` }}
            />
          </span>
        )}
      </span>
      <span className="text-right">
        <Montant valeur={ligne.month} signe={total} fort={sousTotal || total} />
      </span>
      <span className="text-right text-muted-foreground">
        <Montant valeur={ligne.cumulative} signe={total} fort={sousTotal || total} />
      </span>
    </li>
  );
};

/** Libellé d'un chiffre « pour information » (le serveur dit « (hors résultat) » pour les taxes). */
const libelleInfo = (ligne) =>
  ligne.key === 'fuel_sales_tax' ? 'Taxes sur les ventes de carburant' : ligne.label;

/**
 * Pied du relevé: les chiffres donnés pour information, repliés, alignés sur
 * les colonnes du relevé (mois, cumul); l'écart de contrôle non nul en
 * pastille, son détail derrière l'aide.
 */
const ChiffresInformation = ({ infos, ecart }) => {
  const [ouvert, setOuvert] = React.useState(false);
  const idListe = React.useId();
  if (infos.length === 0 && !ecart) return null;
  return (
    <div className="border-t border-border/70" data-testid="chiffres-information">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
        {infos.length > 0 && (
          <button
            type="button"
            onClick={() => setOuvert((v) => !v)}
            aria-expanded={ouvert}
            aria-controls={idListe}
            className="text-xs font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {ouvert ? 'Masquer les autres chiffres' : `${LEXIQUE.autresChiffres} (${infos.length})`}
          </button>
        )}
        {ecart && (
          <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-flame/30 bg-flame/10 py-0.5 pl-2 pr-0.5 text-xs text-flame-ink">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="min-w-0 truncate">{ecart.label}</span>
            <Montant valeur={ecart.month} className="font-semibold" />
            <Aide titre={ecart.label}>
              Ce mois : <Montant valeur={ecart.month} className="text-foreground" />. Depuis le début de l’exercice :{' '}
              <Montant valeur={ecart.cumulative} className="text-foreground" />.
            </Aide>
          </span>
        )}
      </div>
      {ouvert && (
        <dl id={idListe} aria-label={LEXIQUE.autresChiffres} className="divide-y divide-border/50 border-t border-border/50 text-xs text-muted-foreground">
          {infos.map((ligne) => (
            <div key={ligne.key} className="grid grid-cols-[1fr_auto_auto] items-baseline gap-x-4 px-4 py-1.5 sm:grid-cols-[1fr_9rem_9rem]">
              <dt className="min-w-0">{libelleInfo(ligne)}</dt>
              <dd className="text-right">
                <Montant valeur={ligne.month} unite={ligne.unit} />
              </dd>
              <dd className="text-right">
                <Montant valeur={ligne.cumulative} unite={ligne.unit} />
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};

const Releve = ({ report, infos = [], ecart = null }) => {
  const [toutVoir, setToutVoir] = React.useState(false);
  const lignes = report.result_lines || [];
  const repliees = lignes.filter(estRepliable);
  const visibles = toutVoir ? lignes : lignes.filter((l) => !estRepliable(l));
  const max = Math.max(
    0,
    ...lignes.filter((l) => l.nature === 'produit' || l.nature === 'charge').map((l) => Math.abs(montant(l.month))),
  );
  return (
    <section aria-labelledby="titre-releve" className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-border/70 px-4 py-2.5">
        <h3 id="titre-releve" className="text-sm font-semibold text-foreground">
          Résultat
          {report.period?.fiscal_year_label && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">{report.period.fiscal_year_label}</span>
          )}
        </h3>
        {repliees.length > 0 && (
          <button
            type="button"
            onClick={() => setToutVoir((v) => !v)}
            aria-expanded={toutVoir}
            className="text-xs font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {toutVoir
              ? 'Masquer les lignes à zéro'
              : `Voir ${repliees.length} ${pluriel(repliees.length, 'ligne à zéro', 'lignes à zéro')}`}
          </button>
        )}
      </header>
      <div
        className="grid grid-cols-[1fr_auto_auto] gap-x-4 border-b border-border/70 px-4 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground sm:grid-cols-[1fr_9rem_9rem]"
        aria-hidden="true"
      >
        <span />
        <span className="text-right">{report.columns?.month}</span>
        <span className="text-right normal-case tracking-normal">{report.columns?.cumulative}</span>
      </div>
      <ol aria-label={`Relevé du résultat, ${report.columns?.month || 'mois'} et cumul`}>
        {visibles.map((ligne) => (
          <LigneReleve key={ligne.key} ligne={ligne} max={max} />
        ))}
      </ol>
      <ChiffresInformation infos={infos} ecart={ecart} />
    </section>
  );
};

const Tuile = ({ titre, total, legende, children, ton, className }) => (
  <div className={cn('flex min-w-0 flex-col rounded-xl border border-border bg-card p-3.5 shadow-sm', className)}>
    <p className="readout-label">{titre}</p>
    <p className={cn('mt-1.5 text-lg font-semibold tabular-nums', ton)}>
      <Montant valeur={total} />
    </p>
    {legende && <p className="text-xs text-muted-foreground">{legende}</p>}
    {children}
  </div>
);

const ListeSoldes = ({ groupe }) => {
  const lignes = groupe?.top || [];
  if (lignes.length === 0) return null;
  return (
    <ul className="mt-2 divide-y divide-border/60 text-sm">
      {lignes.map((ligne, index) => (
        <li key={`${ligne.name}-${index}`} className="flex items-baseline justify-between gap-3 py-1">
          <span className="min-w-0 truncate">{ligne.name}</span>
          <span className="shrink-0 tabular-nums">{nombre(ligne.amount)}</span>
        </li>
      ))}
      {groupe.parties_count > lignes.length && (
        <li className="py-1 text-xs text-muted-foreground">
          et {groupe.parties_count - lignes.length} {pluriel(groupe.parties_count - lignes.length, 'autre', 'autres')}
        </li>
      )}
    </ul>
  );
};

const aideStock = (stock) => {
  const litres = `${nombre(stock.liters) || '0'} L`;
  const parts = [stock.average_cost ? `${litres} à ${nombre(stock.average_cost)} le litre en moyenne` : litres];
  if (montant(stock.unknown_cost_liters) > 0) {
    parts.push(`dont ${nombre(stock.unknown_cost_liters)} L sans coût d'achat connu`);
  }
  return parts.join(', ');
};

const compte = (n, singulier, plurielTexte) => (n > 0 ? `${n} ${pluriel(n, singulier, plurielTexte)}` : null);

/**
 * Grille de la position sans tuile orpheline: quatre tuiles en 2 × 2 puis de
 * front; cinq tuiles en deux larges puis trois (sur deux colonnes, la
 * cinquième prend toute la largeur).
 */
const GRILLE_POSITION = {
  4: 'sm:grid-cols-2 xl:grid-cols-4',
  5: 'sm:grid-cols-2 lg:grid-cols-6',
};
const LARGEURS_CINQ = ['lg:col-span-3', 'lg:col-span-3', 'lg:col-span-2', 'lg:col-span-2', 'sm:col-span-2 lg:col-span-2'];

const Position = ({ position }) => {
  const tresorerie = position.treasury || {};
  const comptes = tresorerie.accounts || [];
  const nbParties = (g) => Number(g?.parties_count ?? 0);
  const stock = position.fuel_stock;
  const sansSolde = Number(tresorerie.unknown_balance_accounts) || 0;
  const avances = montant(position.customer_credits?.total);
  const tuiles = [
    <Tuile
      key="tresorerie"
      titre={LEXIQUE.argentCaisseBanque}
      total={tresorerie.total ?? 0}
      ton={montant(tresorerie.total) < 0 ? 'text-destructive' : undefined}
      legende={
        sansSolde > 0
          ? `${sansSolde} ${pluriel(sansSolde, "compte n'a pas de solde de départ", "comptes n'ont pas de solde de départ")} : à renseigner.`
          : null
      }
    >
      {comptes.length > 0 && (
        <ul className="mt-2 divide-y divide-border/60 text-sm">
          {comptes.map((ligne, index) => (
            <li key={`${ligne.name}-${index}`} className="flex items-baseline justify-between gap-3 py-1">
              <span className="min-w-0 truncate" title={ligne.name}>
                {ligne.name}
                {ligne.type_label && <span className="ml-1 text-xs text-muted-foreground">({ligne.type_label})</span>}
              </span>
              {ligne.balance === null || ligne.balance === undefined ? (
                <span className="shrink-0 text-xs italic text-muted-foreground">À renseigner</span>
              ) : (
                <Montant valeur={ligne.balance} className="shrink-0" />
              )}
            </li>
          ))}
        </ul>
      )}
    </Tuile>,
    <Tuile
      key="clients"
      titre={LEXIQUE.chezClients}
      total={position.receivables?.total ?? 0}
      legende={[
        compte(nbParties(position.receivables), 'client', 'clients'),
        avances > 0 ? `avances reçues : ${nombre(avances)}` : null,
      ].filter(Boolean).join(' · ') || null}
    >
      <ListeSoldes groupe={position.receivables} />
    </Tuile>,
    <Tuile
      key="fournisseurs"
      titre={LEXIQUE.aPayerFournisseurs}
      total={position.supplier_debts?.total ?? 0}
      legende={compte(nbParties(position.supplier_debts), 'fournisseur', 'fournisseurs')}
    >
      <ListeSoldes groupe={position.supplier_debts} />
    </Tuile>,
    <Tuile
      key="proprietaires"
      titre={LEXIQUE.aPayerProprietaires}
      total={position.owner_debts?.total ?? 0}
      legende={compte(nbParties(position.owner_debts), 'propriétaire', 'propriétaires')}
    >
      <ListeSoldes groupe={position.owner_debts} />
    </Tuile>,
    stock && <Tuile key="stock" titre={libelleStock(stock)} total={stock.value ?? 0} legende={aideStock(stock)} />,
  ].filter(Boolean);
  return (
    <Question
      id="titre-position"
      titre={LEXIQUE.questionOuArgent}
      meta={`au ${formatDate(position.as_of)}`}
      aide="En fin de mois : l’argent en caisse et en banque, ce que les clients vous doivent, ce que vous devez aux fournisseurs et aux propriétaires des camions loués, et la valeur du carburant pas encore vendu."
    >
      <div className={cn('grid gap-3', GRILLE_POSITION[tuiles.length] || 'sm:grid-cols-2')}>
        {tuiles.map((tuile, index) =>
          tuiles.length === 5 ? React.cloneElement(tuile, { className: LARGEURS_CINQ[index] }) : tuile,
        )}
      </div>
    </Question>
  );
};

const etatResultat = (n) => {
  if (n > 0) return { etat: LEXIQUE.benefice, ton: 'positif' };
  if (n < 0) return { etat: LEXIQUE.perte, ton: 'critique' };
  return { etat: LEXIQUE.equilibre, ton: 'neutre' };
};

/** Les quatre chiffres du haut: résultat, argent disponible, ce qu'on vous doit, ce que vous devez. */
export const chiffresBilan = (report) => {
  const unite = report.currency_label || 'FCFA';
  const ligne = (report.result_lines || []).find((l) => l.key === 'result') ||
    (report.result_lines || []).find((l) => l.nature === 'total') || {};
  const resultat = montant(ligne.month);
  const chiffres = [
    {
      cle: 'resultat',
      titre: LEXIQUE.resultatMois,
      valeur: formaterResultat(resultat) || '0',
      unite,
      negatif: resultat < 0,
      ...etatResultat(resultat),
      detail: `Depuis le début de l’exercice : ${formaterResultat(montant(ligne.cumulative)) || '0'} ${unite}`,
      aide: 'Ce que le mois a rapporté, toutes les charges du mois déduites. Le détail ligne par ligne est dans « Qu’est-ce que j’ai gagné ? ».',
    },
  ];
  const position = report.position;
  if (!position) return chiffres;
  const tresorerie = montant(position.treasury?.total);
  const sansSolde = Number(position.treasury?.unknown_balance_accounts) || 0;
  const clients = montant(position.receivables?.total);
  const nbClients = Number(position.receivables?.parties_count) || 0;
  const fournisseurs = montant(position.supplier_debts?.total);
  const proprietaires = montant(position.owner_debts?.total);
  const du = fournisseurs + proprietaires;
  chiffres.push(
    {
      cle: 'tresorerie',
      titre: LEXIQUE.argentDisponible,
      valeur: nombre(tresorerie) || '0',
      unite,
      negatif: tresorerie < 0,
      ...(sansSolde > 0
        ? { etat: `${sansSolde} ${pluriel(sansSolde, 'compte à compléter', 'comptes à compléter')}`, ton: 'attention' }
        : tresorerie < 0
          ? { etat: 'Découvert', ton: 'critique' }
          : {}),
      detail: LEXIQUE.argentCaisseBanque,
    },
    {
      cle: 'clients',
      titre: LEXIQUE.clientsVousDoivent,
      valeur: nombre(clients) || '0',
      unite,
      ...(clients === 0 ? { etat: 'Rien à encaisser', ton: 'positif' } : {}),
      detail: compte(nbClients, 'client', 'clients'),
    },
    {
      cle: 'dettes',
      titre: LEXIQUE.vousDevez,
      valeur: nombre(du) || '0',
      unite,
      ...(du === 0 ? { etat: 'Rien à payer', ton: 'positif' } : {}),
      detail: du === 0 ? null : `Fournisseurs ${nombre(fournisseurs) || '0'} · propriétaires ${nombre(proprietaires) || '0'}`,
    },
  );
  return chiffres;
};

/** Papiers de camion à renouveler dans le mois: autant de lignes que de papiers, donc lus par pages. */
const ListePapiers = ({ papiers }) => {
  const pagination = usePagination(papiers.items, { cle: 'papiers', reinitialiser: papiers.month_label || '' });
  return (
    <>
      <ul className="divide-y divide-border/60 overflow-hidden rounded-xl border border-border bg-card text-sm shadow-sm">
        {pagination.lignes.map((papier, index) => (
          <li
            key={`${papier.truck_id || papier.truck_label}-${papier.document_label}-${pagination.premiere + index}`}
            className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 px-4 py-2"
          >
            <span className="min-w-0">
              <span className="font-medium">{papier.truck_label}</span>
              <span className="text-muted-foreground"> · {papier.document_label}</span>
              {papier.reference && <span className="ml-1 text-xs text-muted-foreground">({papier.reference})</span>}
            </span>
            <span className="tabular-nums">{formatDate(papier.expiry_date)}</span>
          </li>
        ))}
      </ul>
      <BarrePagination pagination={pagination} nom="papiers" />
    </>
  );
};

const AIDE_RESULTAT = [
  'Ventes de carburant hors taxes, moins le coût du carburant vendu, plus le fret et la location, moins chaque charge : c’est le résultat.',
  'Les achats de carburant du mois ne sont pas une charge : le carburant non vendu reste en stock, dans « Où est mon argent ? ».',
];

const MonthlyBalanceReport = ({ report }) => {
  if (!report || !report.result_lines) {
    return (
      <EmptyState
        icon={FileX}
        title="Aucun bilan pour ce mois"
        description="Le bilan de ce mois n'a pas pu être établi."
      />
    );
  }

  const { position, documents_due: papiers } = report;
  const infos = report.info_lines || [];
  const ecart = infos.find((l) => l.key === 'control_gap');
  const ecartNonNul = ecart && (montant(ecart.month) !== 0 || montant(ecart.cumulative) !== 0);
  const autresInfos = infos.filter(
    (l) => l.key !== 'control_gap' && l.key !== 'summary_revenue' && (montant(l.month) !== 0 || montant(l.cumulative) !== 0),
  );
  const notes = (report.notes || []).filter(Boolean);

  return (
    <div className="space-y-6">
      <EncartControleRecettes rapport={report} />
      <ChiffresCles chiffres={chiffresBilan(report)} />

      <Question
        id="question-gagne"
        titre={LEXIQUE.questionGagne}
        aide="Le mois à gauche, le cumul de l’exercice à droite. Les lignes à zéro sont repliées."
        points={notes.length > 0 ? notes : AIDE_RESULTAT}
      >
        <Releve report={report} infos={autresInfos} ecart={ecartNonNul ? ecart : null} />
      </Question>

      {position && <Position position={position} />}

      {papiers && (
        <Question
          id="titre-papiers"
          titre={LEXIQUE.questionBientot}
          meta={`Papiers de camion à renouveler en ${papiers.month_label}`}
          aide={`Les papiers des camions (assurance, visite technique…) qui arrivent à échéance en ${papiers.month_label}. Les papiers déjà échus sont signalés en haut de la page.`}
        >
          {papiers.items?.length > 0 ? (
            <ListePapiers papiers={papiers} />
          ) : (
            <p className="text-sm text-muted-foreground">
              Aucun papier de camion n'arrive à échéance en {papiers.month_label}.
            </p>
          )}
        </Question>
      )}
    </div>
  );
};

export default MonthlyBalanceReport;
